import type { PageSummary } from '@diana/contracts';
import { ExternalLink, ImageIcon } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import './PagesList.css';

export function PagesList({
  pages,
  covers = {},
  changingId,
  error,
  changeSiteAccess,
}: {
  pages: PageSummary[];
  covers?: Record<string, string | undefined>;
  changingId?: string;
  error?: string;
  changeSiteAccess(page: PageSummary, siteAccess: PageSummary['siteAccess']): Promise<void>;
}) {
  return (
    <article className="pages-list">
      <header className="pages-list__heading">
        <h1>Pages</h1>
        <a
          className="pages-list__view-site"
          href="https://perova.photo/"
          target="_blank"
          rel="noreferrer"
        >
          View site
          <ExternalLink size={15} aria-hidden="true" />
        </a>
      </header>
      {pages.length === 0 ? <p className="pages-list__empty">No pages yet.</p> : null}
      {pages.length > 0 ? (
        <div className="pages-list__rows">
          {pages.map((page) => {
            const name = page.slug === 'home' ? 'Homepage' : title(page.slug);
            return (
              <div className="pages-list__row" key={page.id}>
                <Link className="pages-list__link" to={`/pages/${page.id}`}>
                  <PageCover key={covers[page.id] ?? 'empty'} src={covers[page.id]} />
                  <span className="pages-list__copy">
                    <strong>{name}</strong>
                    <small>{page.route}</small>
                  </span>
                </Link>
                <div className="pages-list__access">
                  <span>{page.siteAccess === 'Public' ? 'Public' : 'Admin only'}</span>
                  <button
                    className="pages-list__switch"
                    type="button"
                    role="switch"
                    aria-checked={page.siteAccess === 'Public'}
                    aria-label={`${name} public access`}
                    disabled={changingId === page.id}
                    aria-busy={changingId === page.id || undefined}
                    onClick={() =>
                      void changeSiteAccess(
                        page,
                        page.siteAccess === 'Public' ? 'AdminOnly' : 'Public',
                      )
                    }
                  >
                    <span aria-hidden="true" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
      {error ? (
        <p className="pages-list__error" role="alert">
          {error}
        </p>
      ) : null}
    </article>
  );
}

function title(value: string) {
  return value
    .split('-')
    .map((part) => part[0]!.toUpperCase() + part.slice(1))
    .join(' ');
}

function PageCover({ src }: { src?: string }) {
  const [failed, setFailed] = useState(false);
  return (
    <span className="pages-list__cover" aria-hidden="true">
      {src && !failed ? (
        <img src={src} alt="" loading="lazy" decoding="async" onError={() => setFailed(true)} />
      ) : (
        <ImageIcon size={20} />
      )}
    </span>
  );
}
